import { PropsWithChildren, useEffect, useMemo, useRef } from "react";

import { TransportProvider } from "@connectrpc/connect-query";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";

import { createEdgeTransport } from "@/api/client";
import { createAuthInterceptor, createLoggingInterceptor, getApiBaseUrl } from "@/api/transport";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const AuthenticatedQueryClientProvider = ({ children }: PropsWithChildren) => {
  const { getAccessToken, organizationId, user } = useAuth();
  const authScopeRef = useRef<string | null>(null);

  useEffect(() => {
    const nextAuthScope = user?.id ? `${user.id}:${organizationId ?? "__no_org__"}` : null;

    if (authScopeRef.current && authScopeRef.current !== nextAuthScope) {
      // Cross-tenant/user switches must not reuse cached responses from a previous auth scope.
      queryClient.clear();
    }

    authScopeRef.current = nextAuthScope;
  }, [organizationId, user?.id]);

  const interceptors = useMemo(() => {
    return [createAuthInterceptor(getAccessToken), createLoggingInterceptor()];
  }, [getAccessToken]);

  const transport = useMemo(() => {
    return createEdgeTransport({
      baseUrl: getApiBaseUrl(),
      interceptors,
    });
  }, [interceptors]);

  return (
    <TransportProvider transport={transport}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </TransportProvider>
  );
};

export default AuthenticatedQueryClientProvider;
